import { Flame, Gauge, ListMusic, MapPin, Medal } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { PageHero } from '@/components/dashboard/fx/PageHero'
import { Reveal } from '@/components/dashboard/fx/Reveal'
import { SpotlightCard } from '@/components/dashboard/fx/SpotlightCard'
import { PAGE_WIDTH } from '@/components/dashboard/fx/layout'
import { StatTile } from '@/components/dashboard/StatTile'
import { PartToWholeBreakdown } from '@/components/dashboard/PartToWholeBreakdown'
import { CatalogCoverage } from '@/components/about/CatalogCoverage'
import { CrescendoNote } from '@/components/about/CrescendoNote'
import { useAboutStats } from '@/hooks/useAboutStats'
// Imported (not served from public/) so Vite emits a content-hashed URL --
// swapping the photo then changes the filename automatically and can never
// be masked by a year-long browser/CDN cache of the old one.
import aboutPhoto from '@/images/about-me.jpg'
import { formatPrecise, initials } from '@/lib/format'

const TECH_STACK = [
  'Ruby on Rails',
  'PostgreSQL',
  'Solid Queue',
  'Solid Cache',
  'Solid Cable',
  'React',
  'TypeScript',
  'Vite',
  'Tailwind CSS',
  'shadcn/ui',
  'ActionCable',
  'Active Storage',
  'Kamal',
  'Docker',
  "Last.fm's API",
]

// The same small mono label every section on this page opens with.
function Eyebrow({ children }: { children: React.ReactNode }) {
  return <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">{children}</span>
}

export function AboutPage() {
  const { data, error } = useAboutStats()

  return (
    <div className="overflow-x-clip">
      <PageHero
        eyebrow="About"
        title="Why this"
        accent="exists."
        description="The story behind the project, and what it's actually built on."
      />

      <div className={`${PAGE_WIDTH} flex flex-col gap-6 py-8 sm:py-12`}>
        {/* The story beside its photo -- items-stretch, so the photo is exactly as tall as the
            text and neither leaves a gap under itself. */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-stretch">
          <div className="flex flex-col gap-4 text-lg leading-relaxed">
            <Eyebrow>The story</Eyebrow>
            <p>After 1,000+ rides on my Peloton bike, I started noticing the same songs and artists showing up over and over again.</p>
            <p>
              There are entire genres with endless rides, while others barely seem to exist. As someone who&apos;d happily trade a few pop punk rides for some actual punk, I started wondering whether
              the data backed up what I was hearing.
            </p>
            <p>So I did what any normal person would do and started digging into it.</p>
            <p>
              At first, I mostly wanted to answer a few questions. How repetitive is the catalog really? Which instructors branch out the most? Which themes lean on the same handful of songs? And where
              are the great tracks nobody seems to be playing?
            </p>
            <p>That turned into this project. Which might be nothing. Or I might learn something. I dunno. I think it&apos;s fun though.</p>
          </div>

          <Reveal className="lg:min-h-0">
            <img src={aboutPhoto} alt="Me and my wife on a bridge over a canal" className="aspect-video w-full rounded-3xl border border-border object-cover lg:h-full lg:aspect-auto" />
          </Reveal>
        </div>

        {/* The rider the story is about: a wide strip, so it doesn't stand tall beside the text. */}
        <SpotlightCard className="grid gap-x-12 gap-y-6 p-6 sm:p-8 lg:grid-cols-2 lg:items-center">
          <div className="flex flex-col gap-5">
            <Eyebrow>The rider</Eyebrow>

            {data ? (
              <div className="flex items-center gap-4">
                <Avatar className="size-16 ring-1 ring-border">
                  <AvatarImage src={data.image_url ?? undefined} alt="" />
                  <AvatarFallback>{initials(data.name)}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col gap-0.5">
                  <span className="font-display text-2xl font-semibold tracking-tight">{data.name}</span>
                  <span className="text-sm text-muted-foreground">@{data.username}</span>
                  {data.location && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="size-3" />
                      {data.location}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <Skeleton className="size-16 rounded-full" />
                <Skeleton className="h-8 w-40" />
              </div>
            )}

            {error && <p className="text-sm text-destructive">Couldn&apos;t load Peloton stats: {error}</p>}

            {data ? (
              <div className="grid grid-cols-3 gap-3">
                <StatTile label="Total workouts" value={data.total_workouts} icon={Flame} stacked />
                <StatTile label="Est. cycling FTP" value={data.estimated_cycling_ftp} icon={Gauge} stacked />
                <StatTile label="Badge level" value={data.badge_level} icon={Medal} stacked />
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-[76px] rounded-xl" />
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3">
            {data ? (
              <>
                <h3 className="flex items-center gap-2 text-sm font-semibold">
                  <ListMusic className="size-4" />
                  Workout mix
                </h3>
                <PartToWholeBreakdown data={data.workout_counts} unitLabel="workouts" />
                <p className="text-xs text-muted-foreground">
                  {formatPrecise(data.total_workouts)} total workouts since joining Peloton in {new Date(data.member_since).getFullYear()} -- pulled straight from my own account.
                </p>
              </>
            ) : (
              <Skeleton className="h-[160px] rounded-xl" />
            )}
          </div>
        </SpotlightCard>

        {/* The data backs the story up, so it comes straight after it; the Crescendo aside is
            the fun tangent, and the stack is a footnote. */}
        <CatalogCoverage />

        <CrescendoNote />

        <Reveal>
          <section className="flex flex-col gap-4 border-t border-border/60 pt-8">
            <div className="flex flex-col gap-1">
              <Eyebrow>Under the hood</Eyebrow>
              <h2 className="text-base text-muted-foreground">I&apos;m a software engineer by trade -- here&apos;s what this site is actually built on.</h2>
            </div>
            <ul className="flex flex-wrap gap-2">
              {TECH_STACK.map((tech) => (
                <li key={tech}>
                  <Badge variant="secondary">{tech}</Badge>
                </li>
              ))}
            </ul>
          </section>
        </Reveal>
      </div>
    </div>
  )
}
